// UnitReadOnlySteps.js
//
// The steps of the unit view that show rows and decide nothing (Spec 2 section 6.4):
//
//   Source                where the rows come from, the base's columns with sample values
//                         and how filled each is, and the verdicts raised when the base was
//                         cut (the unit's base_verdicts, which the file chain writes).
//   Rows with no {id}     the rows Placeholders gave a placeholder identifier (time series).
//   Rows after cleaning   the first rows the unit will assert, under the table's columns.

import React from 'react';
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import UnitStepFrame, { RESERVED_COLUMNS, cellText, rowKeyId, rowKeyOf, targetLabel } from './UnitStepFrame';
import { activeValue, rowKeyText } from '../../../utils/decisions';
import { splitHeaderPath } from '../../../utils/headerPaths';
import { countNoun } from '../../../utils/pluralise';

const SAMPLE_VALUES_SHOWN = 3;
// Placeholders gives a row with no identifier `__missing__{unit id}__{row key}`.
const PLACEHOLDER_PREFIX = '__missing__';
const PLACEHOLDER_COLUMNS_SHOWN = 3;

function tableNameOf(table) {
  return String(table.name || table.id || '').replace(/_/g, ' ');
}

// Props: number, file, unit, verdicts (the unit's base_verdicts), fileBlock, onAdjust (opens
// the sheet workspace on this unit's table), onGoToDocument (back to the file view, where a
// document's instructions are added).
export function SourceStep({ number, file, unit, verdicts, fileBlock, onAdjust, onGoToDocument }) {
  const source = unit.source || {};
  const plan = source.sheet && file.processes?.segment ? activeValue(file.processes.segment, source.sheet) : null;
  const region = source.region_id ? (plan?.regions || []).find((candidate) => candidate.region_id === source.region_id) : null;
  const columns = unit.columns || [];
  const stats = unit.column_stats || {};
  const sampleRows = unit.sample_rows || [];
  const fieldEntries = Object.entries(unit.fields || {});
  const partlyScanned = columns
    .map((column) => stats[column])
    .find((columnStats) => columnStats && columnStats.scanned < columnStats.rows);

  let where;
  if (source.record_set_id) {
    const pages = source.pages || [];
    where = `Pages ${pages[0]}–${pages[pages.length - 1]} of the document`;
  } else if (source.fields) {
    where = `The header fields of the ${source.sheet} sheet`;
  } else if (region) {
    const sections = Object.entries(region.section_values || {}).map(([key, value]) => ` · ${key}: ${value}`).join('');
    where = `The ${source.sheet} sheet, rows ${region.body_first_row}–${region.body_last_row}, `
      + `columns ${region.col_first}–${region.col_last}${sections}`;
  } else {
    where = `The ${source.sheet} sheet`;
  }

  return (
    <UnitStepFrame number={number} title="Source" verdicts={verdicts} defaultOpen={verdicts.length > 0}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="body2">{where}</Typography>
        {source.region_id && (
          <Button size="small" variant="outlined" disabled={Boolean(fileBlock)} onClick={onAdjust} sx={{ textTransform: 'none' }}>
            Adjust
          </Button>
        )}
      </Box>
      {fieldEntries.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="caption" color="text.secondary">Header fields:</Typography>
          {fieldEntries.map(([key, value]) => (
            <Chip key={key} size="small" variant="outlined" label={`${key}: ${cellText(value)}`} />
          ))}
        </Box>
      )}
      {columns.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Column</TableCell>
                <TableCell>Sample values</TableCell>
                <TableCell>Filled</TableCell>
                <TableCell>Looks like</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {columns.map((column) => {
                const columnStats = stats[column];
                // What the column looks like, from its parse counts over the scanned rows:
                // numbers, dates or text, or one of the first two with a few exceptions named.
                let look = 'text';
                if (!columnStats || !columnStats.present) {
                  look = 'empty';
                } else {
                  const fails = columnStats.fails || {};
                  const numberFails = fails.Number?.count ?? columnStats.present;
                  const dateFails = fails.Date?.count ?? columnStats.present;
                  const yesNoFails = fails['Yes/No']?.count ?? columnStats.present;
                  const quote = (examples) => (examples || []).map((example) => `'${example}'`).join(', ');
                  if (numberFails === 0) look = 'numbers';
                  else if (dateFails === 0) look = 'dates';
                  else if (yesNoFails === 0) look = 'yes / no';
                  else if (numberFails <= 3 && numberFails < columnStats.present) look = `numbers, except ${quote(fails.Number.examples)}`;
                  else if (dateFails <= 3 && dateFails < columnStats.present) look = `dates, except ${quote(fails.Date.examples)}`;
                }
                const samples = sampleRows
                  .map((row) => row[column])
                  .filter((value) => value !== null && value !== undefined && value !== '')
                  .slice(0, SAMPLE_VALUES_SHOWN);
                return (
                  <TableRow key={column}>
                    <TableCell><Typography variant="body2" fontWeight={600}>{splitHeaderPath(column).join(' › ')}</Typography></TableCell>
                    <TableCell>{samples.map(cellText).join(' · ')}</TableCell>
                    <TableCell>{columnStats ? `${columnStats.present} of ${columnStats.scanned}` : '—'}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color={look.includes('except') ? 'warning.main' : 'text.secondary'}>
                        {look}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary">This table has no columns.</Typography>
      )}
      {partlyScanned && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {`Filled and Looks like are measured over the first ${partlyScanned.scanned.toLocaleString()} `
            + `of ${partlyScanned.rows.toLocaleString()} rows.`}
        </Typography>
      )}
      {source.record_set_id && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
          <Typography variant="body2" sx={{ flex: 1 }}>
            Something wrong in these rows? Tell Decode what to change and it reads the pages again.
          </Typography>
          <Button size="small" variant="outlined" disabled={Boolean(fileBlock)} onClick={onGoToDocument} sx={{ textTransform: 'none' }}>
            Go to the document
          </Button>
        </Box>
      )}
    </UnitStepFrame>
  );
}

function isPlaceholderRow(row, identifierName) {
  return typeof row[identifierName] === 'string' && row[identifierName].startsWith(PLACEHOLDER_PREFIX);
}

// The preview's columns in the order the frame holds them, without the provenance columns.
function previewColumns(preview) {
  const columns = [];
  preview.forEach((row) => {
    Object.keys(row).forEach((column) => {
      if (!RESERVED_COLUMNS.includes(column) && !columns.includes(column)) columns.push(column);
    });
  });
  return columns;
}

// Props: number, unit, table, verdicts.
export function PlaceholdersStep({ number, unit, table, verdicts }) {
  const identifierName = table.identifier?.name;
  const preview = unit.preview || [];
  const placeholderRows = preview.filter((row) => isPlaceholderRow(row, identifierName));
  const otherColumns = previewColumns(preview)
    .filter((column) => column !== identifierName)
    .slice(0, PLACEHOLDER_COLUMNS_SHOWN);
  const missingCount = verdicts.reduce((total, verdict) => total + (verdict.row_count || 0), 0);

  return (
    <UnitStepFrame
      number={number}
      title={`Rows with no ${targetLabel(identifierName || 'identifier')}`}
      verdicts={verdicts}
      record={unit.processes?.placeholders || null}
      defaultOpen={verdicts.length > 0}
    >
      <Typography variant="body2" color="text.secondary">
        {missingCount > 0 || placeholderRows.length > 0
          ? `When this file is checked against ${tableNameOf(table)}, you will be asked about each of these rows: `
            + 'which one it is, or whether to leave it out.'
          : `Every row has a ${targetLabel(identifierName || 'identifier')}.`}
      </Typography>
      {placeholderRows.length > 0 && (
        <TableContainer sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Row</TableCell>
                {otherColumns.map((column) => <TableCell key={column}>{targetLabel(column)}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {placeholderRows.map((row) => (
                <TableRow key={rowKeyId(rowKeyOf(row))}>
                  <TableCell>{rowKeyText(rowKeyOf(row))}</TableCell>
                  {otherColumns.map((column) => <TableCell key={column}>{cellText(row[column])}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </UnitStepFrame>
  );
}

// Props: number, unit, table.
export function PreviewStep({ number, unit, table }) {
  const preview = unit.preview || [];
  const identifierName = table.identifier?.name;
  const columns = previewColumns(preview);
  const rowCount = unit.row_count || 0;

  return (
    <UnitStepFrame number={number} title="Rows after cleaning" defaultOpen>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {`${countNoun(rowCount, 'row')} will go into ${tableNameOf(table)}.`}
        {rowCount > preview.length && preview.length > 0 ? ` The first ${preview.length} are shown.` : ''}
      </Typography>
      {preview.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Row</TableCell>
                {columns.map((column) => <TableCell key={column}>{targetLabel(column)}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.map((row) => {
                const placeholder = isPlaceholderRow(row, identifierName);
                return (
                  <TableRow
                    key={rowKeyId(rowKeyOf(row))}
                    sx={placeholder ? { bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08) } : undefined}
                  >
                    <TableCell>{rowKeyText(rowKeyOf(row))}</TableCell>
                    {columns.map((column) => (
                      <TableCell key={column}>
                        {placeholder && column === identifierName ? <i>(none)</i> : cellText(row[column])}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </UnitStepFrame>
  );
}
